import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  FolderOpen, 
  Newspaper, 
  Mail, 
  Users, 
  LogOut,
  Menu,
  X,
  Plus,
  Bell,
  Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, notificationsAPI } from '../../lib/api';
import { JabalipuraanLogo } from '../../components/common/JabalipuraanLogo';

const sidebarLinks = [
  { href: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { href: '/admin/pending', icon: Clock, label: 'Pending Review' },
  { href: '/admin/articles', icon: FileText, label: 'Articles' },
  { href: '/admin/initiatives', icon: FolderOpen, label: 'Initiatives' },
  { href: '/admin/media', icon: Newspaper, label: 'Media' },
  { href: '/admin/contacts', icon: Mail, label: 'Messages' },
  { href: '/admin/users', icon: Users, label: 'Users' },
];

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, isAdmin, loading, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    if (isAdmin) fetchStats();
  }, [isAdmin]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };
    if (isAdmin) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const isActive = (href, exact = false) => {
    if (exact) return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-muted/30 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 admin-sidebar transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-primary-foreground/10">
            <div className="flex items-center gap-3">
              <JabalipuraanLogo size="sm" linkTo="/" />
              <div>
                <span className="font-heading text-lg font-semibold text-primary-foreground block">
                  Jabalipuraan
                </span>
                <span className="text-xs text-primary-foreground/60">Admin Panel</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {sidebarLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setSidebarOpen(false)}
                className={`admin-sidebar-link ${isActive(link.href, link.exact) ? 'active' : ''}`}
                data-testid={`admin-nav-${link.label.toLowerCase()}`}
              >
                <link.icon className="h-5 w-5 mr-3" />
                {link.label}
                {link.label === 'Messages' && stats?.unread_contacts > 0 && (
                  <span className="ml-auto bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full">
                    {stats.unread_contacts}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-primary-foreground/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                <span className="text-accent font-semibold">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-primary-foreground">{user.name}</p>
                <p className="text-xs text-primary-foreground/60">Administrator</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
              onClick={() => { logout(); navigate('/'); }}
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-background border-b border-border px-4 py-3 flex items-center justify-between">
          <button
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="admin-mobile-menu"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <Link to="/admin/notifications" className="relative p-2 hover:bg-muted rounded-md">
              <Bell className="h-5 w-5 text-muted-foreground" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              View Site →
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

// Dashboard Overview Component
export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: 'Articles', value: stats?.articles || 0, icon: FileText, href: '/admin/articles' },
    { label: 'Initiatives', value: stats?.initiatives || 0, icon: FolderOpen, href: '/admin/initiatives' },
    { label: 'Users', value: stats?.users || 0, icon: Users, href: '/admin/users' },
    { label: 'Unread Messages', value: stats?.unread_contacts || 0, icon: Mail, href: '/admin/contacts' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
          Admin Dashboard
        </h1>
        <p className="text-muted-foreground">
          Manage your heritage platform content and users.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, idx) => (
          <Link
            key={idx}
            to={stat.href}
            className="bg-background p-6 border border-border hover:border-accent/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <stat.icon className="h-8 w-8 text-accent" />
              {loading ? (
                <div className="h-8 w-16 bg-muted animate-pulse"></div>
              ) : (
                <span className="text-3xl font-bold text-foreground">{stat.value}</span>
              )}
            </div>
            <p className="text-muted-foreground font-medium">{stat.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-background p-6 border border-border">
        <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
          Quick Actions
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <Link to="/admin/articles/new">
            <Button className="w-full" data-testid="quick-new-article">
              <Plus className="h-4 w-4 mr-2" />
              New Article
            </Button>
          </Link>
          <Link to="/admin/initiatives/new">
            <Button variant="outline" className="w-full" data-testid="quick-new-initiative">
              <Plus className="h-4 w-4 mr-2" />
              New Initiative
            </Button>
          </Link>
          <Link to="/admin/media/new">
            <Button variant="outline" className="w-full" data-testid="quick-new-media">
              <Plus className="h-4 w-4 mr-2" />
              Add Media
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
